import React, { useEffect, useState, MouseEvent } from "react";
import { useQuery } from "@apollo/client";
import { Link } from "react-router-dom";
import { GET_PLANS } from "./queries";
import { PlanType, SubscriptionProductType } from "../../../models/plans";
import { LoaderBar } from "../../../middleware/loaders";
import Plans from "./plans";
import Payment from "./payment";
import { PageSettingItem } from "../../../models/pageSettings";
import { LoaderDots } from "../../../middleware/main_loader";
import PromoCode from "./promo_code";
import { PromoCodeType } from "../../../models/promocodes";
import { getAuthCookie } from "../../../middleware/cookie-manager";

type Views = "plans" | "payment";

const PurchasePlan: React.FC = () => {
  const user = getAuthCookie(true);
  const { data, loading } = useQuery<{
    plans: PlanType[];
    pageSettings: PageSettingItem[];
  }>(GET_PLANS, { fetchPolicy: "no-cache" });
  const [plans, setPlans] = useState<PlanType[]>([]);
  const [products, setProducts] = useState<SubscriptionProductType[]>([
    {
      id: "1",
      plan_id: "1",
      name: "Flangoo Library™ - Monthly",
      secondary_pricing: 0,
      secondary_pricing_regular: 0,
    },
    {
      id: "2",
      plan_id: "3",
      name: "Flangoo Library™ - Annual",
      secondary_pricing: 0,
      secondary_pricing_regular: 0,
    },
    {
      id: "3",
      plan_id: "3",
      product_id: ["1"],
      name: "Flangoo Creator™ - Annual",
      sub: "(includes Flangoo Library)",
      secondary_pricing: 49,
      secondary_pricing_regular: 149,
    },
    {
      id: "4",
      plan_id: "0",
      product_id: ["1"],
      price: 49,
      name: "Flangoo Creator™ - Add-on",
      secondary_pricing: 49,
      secondary_pricing_regular: 79,
    },
  ]);
  const [promo_code, setPromoCode] = useState<PromoCodeType | undefined>();
  const [selectedPlan, setPlan] = useState<SubscriptionProductType | undefined>(
    undefined
  );
  const [view, setView] = useState<Views>("plans");
  const [is_loyalty, setLoyalty] = useState<boolean>(false);

  useEffect(() => {
    const document_body = document.getElementsByTagName("body")[0];
    if (document_body) {
      document_body.classList.add("white_bg");
    }
    return () => {
      document_body.classList.remove("white_bg");
    };
  }, []);

  useEffect(() => {
    if (data?.plans) {
      ///search for loyalty_pricing inside the plans
      const loyalty = data.plans.find((p) => p.loyalty_price);
      if (loyalty) {
        setLoyalty(true);
      }
      const set_products = products.map((product) => {
        const plan: PlanType | undefined = data.plans.find(
          (p) => p.id === product.plan_id
        );
        if (plan) {
          let price = plan.price_amount + product.secondary_pricing;
          if (promo_code) {
            if (promo_code.type === "discount_cash") {
              price = price - parseFloat(promo_code.value);
            }
          }
          if (loyalty && plan.loyalty_price) {
            price = plan.loyalty_price + product.secondary_pricing;
          }
          return {
            ...product,
            plan,
            price,
          };
        }
        if (product.id === "4") {
          ///if product is add-on set price to 20
          return {
            ...product,
            price: user.plan && showSpecialPrice(user.plan.to) ? 20 : 49,
          };
        }
        return product;
      });
      setProducts(set_products);
      setPlans(data.plans);
    }
    //eslint-disable-next-line
  }, [data]);

  const handlePlanSelect = (ev: MouseEvent<HTMLButtonElement>) => {
    const product_id = ev.currentTarget.getAttribute("data-plan-id");
    if (!product_id) return;
    const product = products.find((p) => p.id === product_id);
    console.log("index.tsx:111 | product", product);
    if (!product) return;
    setPlan(product);
    ///if selected addon product send to payment
    if (product_id === "4") {
      setView("payment");
    }
  };

  const handlePageView = (ev: MouseEvent<HTMLButtonElement>) => {
    const set_view = ev.currentTarget.getAttribute("data-view") as Views;
    if (!set_view) return;
    setView(set_view);
  };

  if (loading) {
    return (
      <main className="first-container bg-light py-5 text-center ">
        <LoaderBar fixed={true} />
        <LoaderDots size={20} />
        <p className="mt-3 text-muted">Getting Plans</p>
      </main>
    );
  }
  let title_txt = "Purchase Your Flangoo Membership";
  if (is_loyalty) {
    title_txt = "Renew Your Membership Today with Loyalty Pricing";
  }

  const onApplyPromoCode = (promo_Code: PromoCodeType) => {
    ///update plans according to promo code
    const new_plans = plans.map((plan) => {
      if (promo_Code.type === "discount_cash" && plan.id !== "1") {
        let loyalty_price = plan.loyalty_price ? plan.loyalty_price : 0;
        let regular_price =
          plan.regular_price > 1 ? plan.regular_price : plan.price_amount;
        let new_price = plan.price_amount - parseFloat(promo_Code.value);
        ///set price to two decimals
        new_price = Math.round(new_price * 100) / 100;
        if (plan.loyalty_price) {
          loyalty_price = plan.loyalty_price - parseFloat(promo_Code.value);
          loyalty_price = Math.round(loyalty_price * 100) / 100;
        }
        return {
          ...plan,
          price_amount: new_price,
          regular_price,
          loyalty_price,
          has_promo: true,
        };
      }
      return plan;
    });
    setPromoCode(promo_Code);
    setPlans(new_plans);
  };

  const removeAppliedCode = () => {
    if (!data || !data.plans) {
      return;
    }
    setPromoCode(undefined);
    setPlans(data.plans);
  };

  const showSpecialPrice = (date: string): boolean => {
    const targetDate = new Date(date);
    const endDate = new Date("2025-09-01");
    return targetDate <= endDate;
  };

  return (
    <main className="first-container bg-light py-5">
      <div className="container">
        <h3 className="black_text">{title_txt}</h3>
        <hr />
        <div className="row justify-content-between">
          {view === "plans" && plans ? (
            <Plans
              plans={plans}
              products={products}
              selectedPlan={selectedPlan}
              handlePlanSelect={handlePlanSelect}
              show_special_price={
                user.plan ? showSpecialPrice(user.plan.to) : false
              }
              is_loyalty={is_loyalty}
            />
          ) : null}
          {view === "payment" && selectedPlan ? (
            <Payment
              selectedPlan={selectedPlan}
              paypal_setting={data?.pageSettings[0]}
              handlePageView={handlePageView}
              promo_code={promo_code}
            />
          ) : null}
          <div className="col-md-10 d-flex justify-content-between align-items-center">
            <Link
              to="/youraccount"
              className="btn btn-dark text-white green_contain_gray"
            >
              <i className="fas fa-angle-left" aria-hidden="true"></i> Go Back
            </Link>
            {view === "plans" ? (
              <PromoCode
                onApplyPromoCode={onApplyPromoCode}
                isHorizontal={true}
                removeAppliedCode={removeAppliedCode}
              />
            ) : null}
            {view === "plans" ? (
              <button
                onClick={handlePageView}
                data-view="payment"
                className="btn btn-outline-danger  btn_lg green_contain_green"
                disabled={selectedPlan ? false : true}
              >
                <i className="fab fa-paypal" aria-hidden="true"></i> Pay Now
              </button>
            ) : null}
          </div>
        </div>
      </div>
    </main>
  );
};

export default PurchasePlan;
